/**
 * Schedule Defaults Calculator
 *
 * Calculates tournament metrics for a cross-group doubles tournament.
 * Handles both classic cross-product matching ('cross') and parallel/zipped
 * matching (like MLP) ('parallel').
 *
 * Time Complexity: O(1) - Constant time execution
 * Space Complexity: O(1) - No extra memory allocation
 *
 * @param {number} teamsPerGroup - Number of teams in each group (e.g. 2
 *   Parsippany teams facing 2 Livingston teams -> teamsPerGroup = 2; a plain
 *   1-team-vs-1-team faceoff -> teamsPerGroup = 1).
 * @param {number} playersPerTeam - Number of players per team (e.g. 4).
 * @param {number} y - Number of times each specific team-pairing repeats.
 * @param {number|null} capPairsPerTeam - Optional cap on unique partnerships
 *   allowed per team-pairing (e.g. 4); defaults to the full mathematical max
 *   (C(playersPerTeam, 2)) when null.
 * @param {string} matchupType - 'cross' (every pair in group A plays every
 *   pair in group B) or 'parallel' (slot-to-slot, like MLP — each
 *   team-pairing gets exactly capPairsPerTeam games, one per partnership).
 * @returns {Object} Structural breakdown of the tournament
 */
export function getDoublesCrossGroupMetricsAdvanced(
  teamsPerGroup,
  playersPerTeam,
  y,
  capPairsPerTeam = null,
  matchupType = 'cross'
) {
  // 1. Calculate unique pairs per team using combinatorics: n * (n - 1) / 2
  const maxPossiblePairs = (playersPerTeam * (playersPerTeam - 1)) / 2;

  // Apply the cap if provided, otherwise use the maximum mathematical pairs
  const pairsPerTeam = capPairsPerTeam !== null
    ? Math.min(maxPossiblePairs, capPairsPerTeam)
    : maxPossiblePairs;

  let totalMatches = 0;

  // 2. Determine total matches based on the structural matchup type
  if (matchupType === 'parallel') {
    // Parallel/Zip pairing (MLP Style):
    // Each team in Group A plays each team in Group B, but they only play
    // 'pairsPerTeam' number of specific slot matchups per faceoff.
    totalMatches = teamsPerGroup * teamsPerGroup * pairsPerTeam * y;
  } else {
    // Cross pairing (Standard Matrix Style):
    // Every unique pair in Group A plays every unique pair in Group B.
    const totalPairsGroupA = teamsPerGroup * pairsPerTeam;
    const totalPairsGroupB = teamsPerGroup * pairsPerTeam;
    totalMatches = totalPairsGroupA * totalPairsGroupB * y;
  }

  // 3. Calculate hardware/player capacity constraints (games allowed simultaneously)
  // A single team of N players can field a maximum of floor(N / 2) doubles matches at once.
  const maxSimultaneousGamesPerTeam = Math.floor(playersPerTeam / 2);
  let gamesPerRound = teamsPerGroup * maxSimultaneousGamesPerTeam;

  // Edge case optimizer: If the total matches are fewer than the maximum physical player capacity
  // (like 1 team vs 1 team in MLP where max player capacity is 2 but only 1 game runs at a time),
  // cap the games per round to the total matches to keep rounds math completely accurate.
  if (matchupType === 'parallel' && gamesPerRound > totalMatches) {
    gamesPerRound = totalMatches;
  }

  // 4. Calculate total rounds needed to clear all matches (rounded up for safety)
  const totalRounds = Math.ceil(totalMatches / gamesPerRound);

  return {
    calculatedPairsPerTeam: pairsPerTeam,
    gamesPerRound,
    totalRounds,
    totalMatches,
  };
}

